import { Scale } from "../../../utils/Scale"
import { MakeVaperImages } from "../MakeYourOwnVaperAssets"
import { MakeVaperSpudPosition, MakeVaperSpudSize } from "../types/MakeVaperScreenValueTypes"

/**
 * Contains the x/y positions and deltas for the various parts of Mr. Spud.
 */
export class MakeVaperSpudPositions {
    static get spriteTitlePositions(): Map<MakeVaperImages, string> {
        return new Map([
            [MakeVaperImages.SMART, "Smart"],
            [MakeVaperImages.DUNCE, "Not Smart"],
            [MakeVaperImages.SUNGLASSES, "Cool"],
            [MakeVaperImages.BIB, "Not Cool"],
            [MakeVaperImages.BUFF_ARMS, "Athletic"],
            [MakeVaperImages.WEAK_ARMS, "Not Athletic"],
            [MakeVaperImages.HAPPY_POTATOES, "Popular"],
            [MakeVaperImages.SAD_POTATOES, "Not Popular"],
            [MakeVaperImages.EXCITED_POTATOES, "Exciting"],
            [MakeVaperImages.BORED_POTATOES, "Not Exciting"],
        ])
    }

    static get spudPositions(): Map<MakeVaperSpudPosition, {x: number, y: number}> {
        const y = Scale.y(0.385)
        return new Map([
            [MakeVaperSpudPosition.RIGHT, {x: Scale.x(0.655), y}],
            [MakeVaperSpudPosition.CENTER, {x: Scale.x(0.4), y}],
            [MakeVaperSpudPosition.LEFT, {x: Scale.x(0.19), y}]
        ])
    }

    static get spudEcigPositions(): Map<MakeVaperSpudSize, {xDelta: number, yDelta: number}> {
        return new Map([
            [MakeVaperSpudSize.LARGE, {xDelta: Scale.x(0.075), yDelta: Scale.y(0.09)}],
            [MakeVaperSpudSize.MEDIUM, {xDelta: Scale.x(0.055), yDelta: Scale.y(0.08)}],
            [MakeVaperSpudSize.SMALL, {xDelta: Scale.x(0.03), yDelta: Scale.y(0.05)}]
        ])
    }

    static get spudEcigScale(): Map<MakeVaperSpudSize, number> {
        return new Map([
            [MakeVaperSpudSize.LARGE, 0.3],
            [MakeVaperSpudSize.MEDIUM, 0.2],
            [MakeVaperSpudSize.SMALL, 0.1]
        ])
    }

    static get spudPartPositions(): Map<MakeVaperSpudSize, Map<string, {xDelta: number, yDelta: number}>> {
        return new Map([
            [MakeVaperSpudSize.LARGE, this.largeSpudPartPositions],
            [MakeVaperSpudSize.MEDIUM, this.mediumSpudPartPositions],
            [MakeVaperSpudSize.SMALL, this.smallSpudPartPositions]
        ])
    }

    private static get largeSpudPartPositions(): Map<string, {xDelta: number, yDelta: number}> {
        return new Map([
            [MakeVaperImages.SMART, {xDelta: Scale.x(0.04), yDelta: -Scale.y(0.15)}],
            [MakeVaperImages.DUNCE, {xDelta: Scale.x(0.055), yDelta: -Scale.y(0.27)}],
            [MakeVaperImages.SUNGLASSES, {xDelta: -Scale.x(0.001), yDelta: -Scale.y(0.04)}],
            [MakeVaperImages.BIB, {xDelta: Scale.x(0), yDelta: Scale.y(0.22)}],
            [MakeVaperImages.BUFF_ARMS, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.07)}],
            [MakeVaperImages.WEAK_ARMS, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.09)}],
            [MakeVaperImages.HAPPY_POTATOES, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.2)}],
            [MakeVaperImages.SAD_POTATOES, {xDelta: Scale.x(0.11), yDelta: -Scale.y(0.34)}],
            [MakeVaperImages.EXCITED_POTATOES, {xDelta: -Scale.x(0.13), yDelta: -Scale.y(0.32)}],
            [MakeVaperImages.BORED_POTATOES, {xDelta: -Scale.x(0.13), yDelta: -Scale.y(0.33)}],
        ])
    }

    private static get mediumSpudPartPositions(): Map<string, {xDelta: number, yDelta: number}> {
        return new Map([
            [MakeVaperImages.SMART, {xDelta: Scale.x(0.04), yDelta: -Scale.y(0.14)}],
            [MakeVaperImages.DUNCE, {xDelta: Scale.x(0.05), yDelta: -Scale.y(0.24)}],
            [MakeVaperImages.SUNGLASSES, {xDelta: -Scale.x(0.001), yDelta: -Scale.y(0.03)}],
            [MakeVaperImages.BIB, {xDelta: Scale.x(0), yDelta: Scale.y(0.2)}],
            [MakeVaperImages.BUFF_ARMS, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.06)}],
            [MakeVaperImages.WEAK_ARMS, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.08)}],
            [MakeVaperImages.HAPPY_POTATOES, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.18)}],
            [MakeVaperImages.SAD_POTATOES, {xDelta: Scale.x(0.11), yDelta: -Scale.y(0.34)}],
            [MakeVaperImages.EXCITED_POTATOES, {xDelta: -Scale.x(0.13), yDelta: -Scale.y(0.3)}],
            [MakeVaperImages.BORED_POTATOES, {xDelta: -Scale.x(0.13), yDelta: -Scale.y(0.3)}],
        ])
    }

    private static get smallSpudPartPositions(): Map<string, {xDelta: number, yDelta: number}> {
        return new Map([
            [MakeVaperImages.SMART, {xDelta: Scale.x(0.025), yDelta: -Scale.y(0.075)}],
            [MakeVaperImages.DUNCE, {xDelta: Scale.x(0.03), yDelta: -Scale.y(0.15)}],
            [MakeVaperImages.SUNGLASSES, {xDelta: -Scale.x(0.001), yDelta: -Scale.y(0.02)}],
            [MakeVaperImages.BIB, {xDelta: Scale.x(0), yDelta: Scale.y(0.125)}],
            [MakeVaperImages.BUFF_ARMS, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.04)}],
            [MakeVaperImages.WEAK_ARMS, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.05)}],
            [MakeVaperImages.HAPPY_POTATOES, {xDelta: -Scale.x(0.001), yDelta: Scale.y(0.11)}],
            [MakeVaperImages.SAD_POTATOES, {xDelta: Scale.x(0.08), yDelta: -Scale.y(0.18)}],
            [MakeVaperImages.EXCITED_POTATOES, {xDelta: -Scale.x(0.09), yDelta: -Scale.y(0.17)}],
            [MakeVaperImages.BORED_POTATOES, {xDelta: -Scale.x(0.09), yDelta: -Scale.y(0.17)}],
        ])
    }
}
